'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

// Single delegated pointer listener for the document. When a card-class
// element is hovered, set CSS custom properties (--tilt-x/y, --glow-x/y) on it
// so the matching rules in globals.css read those values for 3D tilt + glow.
//
// One rAF gate so we update at most once per frame regardless of pointer move
// rate. Reset on pointerleave so cards return to flat smoothly.
//
// Skipped on /admin, on touch, and on reduced-motion.

const CARD_SELECTOR =
  '.featured-card, .ai-card, .service-card, .post-card, .small-card, .cap-tile';
const TILT_MAX = 5.5; // degrees; max rotateX/Y

export function CardTiltEffect() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (pathname?.startsWith('/admin')) return;

    const fine = window.matchMedia('(pointer: fine)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!fine.matches || motion.matches) return;

    let activeCard: HTMLElement | null = null;
    let pendingX = 0;
    let pendingY = 0;
    let pendingPx = 0;
    let pendingPy = 0;
    let rafQueued = false;

    function reset(card: HTMLElement | null) {
      if (!card) return;
      card.style.removeProperty('--tilt-x');
      card.style.removeProperty('--tilt-y');
      card.style.removeProperty('--glow-x');
      card.style.removeProperty('--glow-y');
    }

    function flush() {
      rafQueued = false;
      if (!activeCard) return;
      activeCard.style.setProperty('--tilt-x', `${pendingX.toFixed(2)}deg`);
      activeCard.style.setProperty('--tilt-y', `${pendingY.toFixed(2)}deg`);
      activeCard.style.setProperty('--glow-x', `${pendingPx.toFixed(1)}%`);
      activeCard.style.setProperty('--glow-y', `${pendingPy.toFixed(1)}%`);
    }

    function onPointerMove(e: PointerEvent) {
      const target = (e.target as HTMLElement | null)?.closest?.(CARD_SELECTOR);
      if (!(target instanceof HTMLElement)) {
        if (activeCard) {
          reset(activeCard);
          activeCard = null;
        }
        return;
      }
      if (activeCard && activeCard !== target) {
        reset(activeCard);
      }
      activeCard = target;
      const rect = target.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      pendingX = (0.5 - py) * TILT_MAX;
      pendingY = (px - 0.5) * TILT_MAX;
      pendingPx = px * 100;
      pendingPy = py * 100;
      if (!rafQueued) {
        rafQueued = true;
        requestAnimationFrame(flush);
      }
    }

    function onLeaveDoc() {
      if (activeCard) {
        reset(activeCard);
        activeCard = null;
      }
    }

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerleave', onLeaveDoc);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerleave', onLeaveDoc);
      if (activeCard) reset(activeCard);
    };
  }, [pathname]);

  return null;
}
